import { NATIVE_MINT } from "@solana/spl-token";

import devnetIdl from "../idl/arcane-devnet.json";
import mainnetIdl from "../idl/arcane.json";

export type Network = "mainnet" | "devnet";

export const MAINNET_PROGRAM_ID = mainnetIdl.address;
export const DEVNET_PROGRAM_ID = devnetIdl.address;

export function resolveNetworkFromRpcEndpoint(rpcEndpoint: string): Network {
  const lower = rpcEndpoint.toLowerCase();
  if (lower.includes("devnet") || lower.includes("dev")) {
    return "devnet";
  }
  return "mainnet";
}

export function getProgramIdForNetwork(network: Network): string {
  return network === "devnet" ? DEVNET_PROGRAM_ID : MAINNET_PROGRAM_ID;
}

export function getIdlForNetwork(network: Network) {
  return network === "devnet" ? devnetIdl : mainnetIdl;
}

/** Arcane indexer base URLs (no trailing slash). */
export const MAINNET_INDEXER_URL = "https://idx.arcaneprivacy.com";
export const DEVNET_INDEXER_URL = "https://indexer.arcaneprivacy.com";

export const INDEXER_URLS_BY_NETWORK = {
  mainnet: MAINNET_INDEXER_URL,
  devnet: DEVNET_INDEXER_URL,
} as const;

export function getIndexerUrlForNetwork(network: Network): string {
  return INDEXER_URLS_BY_NETWORK[network];
}

/** Risk API base URLs (no trailing slash). Not used by the Switchboard oracle job. */
export const MAINNET_RISK_API_URL = "https://range.arcaneprivacy.com";
export const DEVNET_RISK_API_URL = "https://risk.arcaneprivacy.com";

export const RISK_API_URLS_BY_NETWORK = {
  mainnet: MAINNET_RISK_API_URL,
  devnet: DEVNET_RISK_API_URL,
} as const;

export function getRiskApiUrlForNetwork(network: Network): string {
  return RISK_API_URLS_BY_NETWORK[network];
}

export const MAINNET_TOKEN_MINTS = {
  Arcane: "3jSL5nnnYcRLmTqFmwvY1Mikh42fQsT7p7NvXPYzanon",
  Bitcoin: "3NZ9JMVBmGAqocybic2c7LQCJScmgsAZ6vQqTDzcqmJh",
  Ethereum: "7vfCXTUXx5WJV5JADk17DUJ4ksgau7utNKj4b963voxs",
  USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
} as const;

export const DEVNET_TOKEN_MINTS = {
  Arcane: "ARCxcL6oX2pd4bKmUxhdoARaaHdKU6TJrTTthPANPQtQ",
  Bitcoin: "",
  Ethereum: "",
  USDC: "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU",
} as const;

export const TOKEN_MINTS_BY_NETWORK = {
  mainnet: MAINNET_TOKEN_MINTS,
  devnet: DEVNET_TOKEN_MINTS,
} as const;

export type TokenSymbol = keyof typeof MAINNET_TOKEN_MINTS;

export function getTokenMintsForNetwork(network: Network) {
  const mints = TOKEN_MINTS_BY_NETWORK[network];
  return {
    Solana: NATIVE_MINT.toBase58(),
    Arcane: mints.Arcane,
    Bitcoin: mints.Bitcoin,
    Ethereum: mints.Ethereum,
    USDC: mints.USDC,
  };
}

export function getArcaneTokenMintForNetwork(network: Network): string {
  return TOKEN_MINTS_BY_NETWORK[network].Arcane;
}

export const TOKEN_DECIMALS = {
  Solana: 9,
  Arcane: 9,
  Ethereum: 8,
  Bitcoin: 8,
  USDC: 6,
} as const;

export type TokenDecimalsSymbol = keyof typeof TOKEN_DECIMALS;

export function getTokenDecimals(symbol: TokenDecimalsSymbol): number {
  return TOKEN_DECIMALS[symbol];
}

/** Supported deposit denominations per token (same on mainnet and devnet). */
export const DEPOSIT_AMOUNTS = {
  Solana: [0.1, 1, 5, 10, 50, 100, 500],
  // Arcane: [1, 10, 50, 100, 500, 1000, 10000, 100000, 1000000],
  Arcane: [10000, 50000, 100000, 500000, 1000000, 10000000],
  Bitcoin: [0.01, 0.05, 0.1, 0.5, 1, 5],
  Ethereum: [0.1, 1, 10, 50, 100, 500],
  USDC: [1, 10, 50, 100, 1000, 10000, 100000, 1000000],
} as const;

export type DepositTokenSymbol = keyof typeof DEPOSIT_AMOUNTS;

export function getDepositAmounts(symbol: DepositTokenSymbol): readonly number[] {
  return DEPOSIT_AMOUNTS[symbol];
}

export function formatNetworkLabel(network: Network): string {
  return network === "mainnet" ? "Mainnet" : "Devnet";
}

/** Max deposit instructions combined into one on-chain transaction. */
export const DEPOSIT_TX_BATCH_SIZE = 8;

/** Max notes per backup transaction (single backupNotes ix per tx). */
export const BACKUPNOTE_TX_BATCH_SIZE = 4;

/** Switchboard queue account for deposit oracle verification. */
export const DEPOSIT_QUEUE_BY_NETWORK = {
  mainnet: "A43DyUGA7s8eXPxqEjJY6EBu1KKbNgfxF8h17VAHn13w",
  devnet: "EYiAmGSdsQTuCw413V5BzaruWuCCSDgTPtBGvLkXHbe7",
} as const satisfies Record<Network, string>;

export function getDepositQueueForNetwork(network: Network): string {
  return DEPOSIT_QUEUE_BY_NETWORK[network];
}

/** Address lookup table for batched deposit transactions (v0). */
export const DEPOSIT_LOOKUP_TABLE_BY_NETWORK = {
  mainnet: "A7nibLxqNhL1zg4MXC6Q2Qg9wR754HNEqd5NYEhkRMw3",
  devnet: "G3PfLv9F1fCmQpDB8MEZEA7mVpcog3KzkHW5V8QtwTp2",
} as const satisfies Record<Network, string>;

export function getDepositLookupTableForNetwork(network: Network): string {
  return DEPOSIT_LOOKUP_TABLE_BY_NETWORK[network];
}
